import { currentSRPSchemaVersion } from "../models/SRPSchema";

export type FormatValidationResult = {
    isValid: boolean;
    errors: string[];
};

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
    return typeof value === "object" && value !== null && !Array.isArray(value);
};

const errorMsg = (field: string, expected: string): string =>
    `${field}: expected ${expected}`;

const checkString = (value: unknown, field: string): string[] =>
    typeof value !== "string" ? [errorMsg(field, "string")] : [];

const checkBoolean = (value: unknown, field: string): string[] =>
    typeof value !== "boolean" ? [errorMsg(field, "boolean")] : [];

const checkNumber = (value: unknown, field: string): string[] =>
    typeof value !== "number" ? [errorMsg(field, "number")] : [];

const checkArrayOfStrings = (value: unknown, field: string): string[] => {
    if (!Array.isArray(value)) {
        return [errorMsg(field, "array of string")];
    }
    if ((value as unknown[]).some((entry) => typeof entry !== "string")) {
        return [errorMsg(field, "array of string")];
    }
    return [];
};

const checkExactString = (value: unknown, field: string, expectedValue: string): string[] => {
    if (typeof value !== "string") {
        return [errorMsg(field, "string")];
    }
    return value !== expectedValue ? [`${field}: expected "${expectedValue}"`] : [];
};

const validateSelectionCriteria = (criteria: unknown, path: string): string[] => {
    if (!isPlainObject(criteria)) {
        return [errorMsg(path, "object")];
    }
    return [
        ...checkString(criteria.modelYear, `${path}.modelYear`),
        ...checkString(criteria.programCode, `${path}.programCode`),
        ...checkString(criteria.ncPartNumber, `${path}.ncPartNumber`),
        ...checkString(criteria.mfalCode, `${path}.mfalCode`),
        ...checkString(criteria.did, `${path}.did`),
    ];
};

const validateCluster = (cluster: unknown, path: string): string[] => {
    if (!isPlainObject(cluster)) {
        return [errorMsg(path, "object")];
    }

    const errors: string[] = [
        ...checkString(cluster.clusterName, `${path}.clusterName`),
        ...checkString(cluster.changeLevel, `${path}.changeLevel`),
        ...checkString(cluster.clusterVersion, `${path}.clusterVersion`),
        ...checkString(cluster.swPartNumber, `${path}.swPartNumber`),
        ...checkString(cluster.clusterUrl, `${path}.clusterUrl`),
        ...checkString(cluster.clusterType, `${path}.clusterType`),
        ...checkString(cluster.configurationType, `${path}.configurationType`),
        ...checkString(cluster.ecuExtractVersion, `${path}.ecuExtractVersion`),
        ...checkString(cluster.f163D2SpecVersion, `${path}.f163D2SpecVersion`),
        ...checkString(cluster.part2SpecUrl, `${path}.part2SpecUrl`),
        ...checkString(cluster.wersConcernNum, `${path}.wersConcernNum`),
        ...checkString(cluster.actionType, `${path}.actionType`),
        ...checkString(cluster.updateType, `${path}.updateType`),
        ...checkString(cluster.sessionSequence, `${path}.sessionSequence`),
        ...checkString(cluster.swDownloadMethod, `${path}.swDownloadMethod`),
        ...checkString(cluster.swSignRequest, `${path}.swSignRequest`),
        ...checkString(cluster.swSigningId, `${path}.swSigningId`),
        ...checkString(cluster.ucmPackagePath, `${path}.ucmPackagePath`),
        ...checkString(cluster.programmingMethod, `${path}.programmingMethod`),
        ...checkBoolean(cluster.inhibitRequired, `${path}.inhibitRequired`),
        ...checkBoolean(cluster.assemblyCritical, `${path}.assemblyCritical`),
        ...checkBoolean(cluster.productionReleaseIntent, `${path}.productionReleaseIntent`),
        ...checkNumber(cluster.swInstallationSequence, `${path}.swInstallationSequence`),
        ...checkNumber(cluster.updateTimeInSeconds, `${path}.updateTimeInSeconds`),
        ...checkArrayOfStrings(cluster.f113ECUAssemblyNum, `${path}.f113ECUAssemblyNum`),
        ...checkArrayOfStrings(cluster.f111CoreAssemblyNum, `${path}.f111CoreAssemblyNum`),
        ...checkArrayOfStrings(cluster.udsMethod, `${path}.udsMethod`),
    ];

    if (Array.isArray(cluster.selectionCriteriaList)) {
        for (let index = 0; index < (cluster.selectionCriteriaList as unknown[]).length; index += 1) {
            errors.push(
                ...validateSelectionCriteria(
                    (cluster.selectionCriteriaList as unknown[])[index],
                    `${path}.selectionCriteriaList[${index}]`
                )
            );
        }
    } else {
        errors.push(errorMsg(`${path}.selectionCriteriaList`, "array"));
    }

    return errors;
};

const validateVariant = (variant: unknown, path: string): string[] => {
    if (!isPlainObject(variant)) {
        return [errorMsg(path, "object")];
    }

    const errors: string[] = [
        ...checkString(variant.variantName, `${path}.variantName`),
        ...checkString(variant.logicalDiagAddress, `${path}.logicalDiagAddress`),
        ...checkString(variant.ucmSubordinateId, `${path}.ucmSubordinateId`),
        ...checkString(variant.flashingAdapterAddress, `${path}.flashingAdapterAddress`),
        ...checkString(variant.flashCapability, `${path}.flashCapability`),
        ...checkString(variant.publicTopology, `${path}.publicTopology`),
        ...checkString(variant.privateTopology, `${path}.privateTopology`),
        ...checkString(variant.asilRating, `${path}.asilRating`),
        ...checkBoolean(variant.arxmlRequired, `${path}.arxmlRequired`),
        ...checkBoolean(variant.vspRequired, `${path}.vspRequired`),
        ...checkBoolean(variant.otaCapable, `${path}.otaCapable`),
        ...checkArrayOfStrings(variant.commsProtocol, `${path}.commsProtocol`),
        ...checkArrayOfStrings(variant.ethernetTopology, `${path}.ethernetTopology`),
    ];

    if (Array.isArray(variant.clusterList)) {
        for (let index = 0; index < (variant.clusterList as unknown[]).length; index += 1) {
            errors.push(
                ...validateCluster(
                    (variant.clusterList as unknown[])[index],
                    `${path}.clusterList[${index}]`
                )
            );
        }
    } else {
        errors.push(errorMsg(`${path}.clusterList`, "array"));
    }

    return errors;
};

const validateMachine = (machine: unknown, path: string): string[] => {
    if (!isPlainObject(machine)) {
        return [errorMsg(path, "object")];
    }

    const errors: string[] = [
        ...checkString(machine.machineName, `${path}.machineName`),
        ...checkString(machine.machineType, `${path}.machineType`),
        ...checkString(machine.canDiagRxId, `${path}.canDiagRxId`),
        ...checkString(machine.canDiagTxId, `${path}.canDiagTxId`),
    ];

    if (Array.isArray(machine.variantList)) {
        if ((machine.variantList as unknown[]).length === 0) {
            errors.push(`${path}.variantList: expected at least one variant`);
        }
        for (let index = 0; index < (machine.variantList as unknown[]).length; index += 1) {
            errors.push(
                ...validateVariant(
                    (machine.variantList as unknown[])[index],
                    `${path}.variantList[${index}]`
                )
            );
        }
    } else {
        errors.push(errorMsg(`${path}.variantList`, "array"));
    }

    return errors;
};

const validateModuleInformation = (moduleInformation: unknown): string[] => {
    if (!isPlainObject(moduleInformation)) {
        return [errorMsg("moduleInformation", "object")];
    }
    return [
        ...checkString(moduleInformation.moduleName, "moduleInformation.moduleName"),
        ...checkArrayOfStrings(moduleInformation.vehiclePrograms, "moduleInformation.vehiclePrograms"),
        ...checkArrayOfStrings(moduleInformation.lveas, "moduleInformation.lveas"),
        ...checkString(moduleInformation.gpdsMilestone, "moduleInformation.gpdsMilestone"),
        ...checkBoolean(moduleInformation.publicRoadWorthy, "moduleInformation.publicRoadWorthy"),
        ...checkArrayOfStrings(moduleInformation.criticalAttributesImpacted, "moduleInformation.criticalAttributesImpacted"),
        ...checkBoolean(moduleInformation.cmaEnabled, "moduleInformation.cmaEnabled"),
    ];
};

const validatePreassemblyImage = (preassemblyImage: unknown): string[] => {
    if (!isPlainObject(preassemblyImage)) {
        return [errorMsg("preassemblyImage", "object")];
    }

    const errors: string[] = [
        ...checkString(preassemblyImage.preassemblyImageName, "preassemblyImage.preassemblyImageName"),
        ...checkString(preassemblyImage.preassemblyImageVersion, "preassemblyImage.preassemblyImageVersion"),
        ...checkString(preassemblyImage.preassemblyImageUrl, "preassemblyImage.preassemblyImageUrl"),
        ...checkString(preassemblyImage.ucmPackagePath, "preassemblyImage.ucmPackagePath"),
        ...checkArrayOfStrings(preassemblyImage.f111coreAssemblyNum, "preassemblyImage.f111coreAssemblyNum"),
    ];

    if (Array.isArray(preassemblyImage.clusterList)) {
        for (let index = 0; index < (preassemblyImage.clusterList as unknown[]).length; index += 1) {
            const cluster = (preassemblyImage.clusterList as unknown[])[index];
            if (!isPlainObject(cluster)) {
                errors.push(errorMsg(`preassemblyImage.clusterList[${index}]`, "object"));
            } else {
                errors.push(
                    ...checkString(cluster.clusterName, `preassemblyImage.clusterList[${index}].clusterName`),
                    ...checkString(cluster.clusterVersion, `preassemblyImage.clusterList[${index}].clusterVersion`)
                );
            }
        }
    } else {
        errors.push(errorMsg("preassemblyImage.clusterList", "array"));
    }

    return errors;
};

export const validateSRPdGenericFormat = (data: unknown): FormatValidationResult => {
    if (!isPlainObject(data)) {
        return { isValid: false, errors: [errorMsg("root", "object")] };
    }

    const errors: string[] = [
        ...checkString(data.srpdName, "srpdName"),
        ...checkString(data.srpdVersion, "srpdVersion"),
        ...checkString(data.srpAssignee, "srpAssignee"),
        ...checkString(data.releaseObjectKey, "releaseObjectKey"),
        ...checkString(data.fixVersion, "fixVersion"),
        ...checkString(data.buildNumber, "buildNumber"),
        ...checkExactString(data.srpSchemaVersion, "srpSchemaVersion", currentSRPSchemaVersion),
        ...checkArrayOfStrings(data.srpWatchers, "srpWatchers"),
        ...checkArrayOfStrings(data.electricalArchitecture, "electricalArchitecture"),
        ...validateModuleInformation(data.moduleInformation),
    ];

    if (Array.isArray(data.machineList)) {
        if ((data.machineList as unknown[]).length === 0) {
            errors.push("machineList: expected at least one machine");
        }
        for (let index = 0; index < (data.machineList as unknown[]).length; index += 1) {
            errors.push(
                ...validateMachine((data.machineList as unknown[])[index], `machineList[${index}]`)
            );
        }
    } else {
        errors.push(errorMsg("machineList", "array"));
    }

    errors.push(...validatePreassemblyImage(data.preassemblyImage));

    return { isValid: errors.length === 0, errors };
};